import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import * as moment from 'moment';
import { map, switchMap } from 'rxjs/operators';
import { TopToolbarService } from 'src/admin-front/ajs-upgraded-providers';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { SearchBarService } from 'src/app/shared/components/search-bar/search-bar.service';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { SiteV2 } from 'src/app/shared/models';
import { DateTimezonePipe } from 'src/app/shared/pipes/date-timezone.pipe';
import { API_URL_COMPANIES, BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { UploadService } from 'src/app/shared/services/upload.service';
import { KakaotalkService } from 'src/app/shared/strategies/channels';
import { KakaotalkTemplateButtonTypeEnum } from './shared/enums/kakaotalk-template-button-type.enum';
import {
  KakaotalkTemplate,
  KakaotalkTemplateButton,
  KakaotalkTemplateForm,
  KakaotalkTemplateItem,
} from './shared/models';

@Injectable({
  providedIn: 'root',
})
export class KakaotalkTemplatesService {
  headerMediaFileBS: BehaviorSubject<{ media_id: number; media_url: string }> = new BehaviorSubject(null);
  kakaotalkAccounts: SiteV2[] = [];
  createTemplateSiteIdUpdatedBS: BehaviorSubject<number> = new BehaviorSubject(null);

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private errorHandlingService: ErrorHandlingService,
    private httpClient: HttpClient,
    private searchBarService: SearchBarService,
    private basicService: BasicService,
    private dateTimezonePipe: DateTimezonePipe,
    private sitesService: SitesService,
    private topToolbarService: TopToolbarService,
    private uploadService: UploadService
  ) {}

  getLimitations(): { maxLength; maxNumber } {
    return {
      maxLength: {
        templateName: 512,
        body: 1024,
        button: 25,
        url: 1500, // url + example should not exceed 2000 caracters.
        example: 500,
      },
      maxNumber: {
        button: 5,
        placeholders: 5,
      },
    };
  }

  retrieveTemplates = (): Observable<HttpResponse<KakaotalkTemplate[]>> => {
    const body = this.searchBarService.buildCriteria();
    return this.httpClient
      .post<KakaotalkTemplate[]>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/kakaotalk/templates`,
        body,
        {
          observe: 'response',
        }
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  };

  getTemplateDetailsPath(languageGroupId: string, siteId?: number) {
    const path = `/#/${this.alcmeonContextService.companyId}/fuse/parameters/message-templates/kakaotalk/${siteId}/template/${languageGroupId}`;
    return this.basicService.fullURLForRoutePath(path);
  }

  /**
   * Allows to show the details for a given template.
   * In case of new template it uses `create`.
   * @remarks It uses a not recommended mechanism due to the hybrid architecture and the communication between the both version of Angular.
   * @param id the id to reach.
   */
  /**
   * @deprecated To remove once angularjs is fully migrated.
   */
  navigateToDetails(id: string, siteId: number) {
    this.basicService.window.location.href = this.getTemplateDetailsPath(id, siteId);
  }

  navigateToCreate() {
    const path = `/#/${this.alcmeonContextService.companyId}/fuse/parameters/message-templates/kakaotalk/template`;
    this.basicService.window.location.href = this.basicService.fullURLForRoutePath(path);
  }

  /**
   * Allows to show the templates.
   * @deprecated To remove once angularjs is fully migrated.
   */
  navigateToList() {
    const path = `/#/${this.alcmeonContextService.companyId}/fuse/parameters/message-templates?type=${SiteTypeEnum.Kakaotalk}`;
    const url = this.basicService.fullURLForRoutePath(path);
    this.basicService.window.location.href = url;
  }

  findAccounts() {
    if (this.kakaotalkAccounts?.length) {
      return of(this.kakaotalkAccounts);
    }
    const kakaotalkAccountType = new KakaotalkService().getType();

    this.kakaotalkAccounts = this.sitesService.sitesV2
      .filter(account => account.type === kakaotalkAccountType)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return of(this.kakaotalkAccounts);
  }

  private formatButtonUrl = (button: KakaotalkTemplateButton) => {
    if (button.type === KakaotalkTemplateButtonTypeEnum.URL && Array.isArray(button.example) && button.example[0]) {
      button.example = button.example[0] as any;
    }
  };

  private formatTemplates = (templates: KakaotalkTemplateItem[], accounts: SiteV2[]): KakaotalkTemplateForm[] => {
    const formatted = templates.reduce((aggregation, template) => {
      const key = `${template.name}|${template.site_id}`;
      aggregation.set(key, (aggregation.get(key) || []).concat(template));
      return aggregation;
    }, new Map<string, KakaotalkTemplateItem[]>());
    const templatesAggregatted: KakaotalkTemplateForm[] = [];
    formatted.forEach((languages, key) => {
      const [name] = key.split('|');
      const category = (languages.find(language => language.category)?.category as any).toLowerCase();
      const lastModificationDate = (languages || []).sort((a, b) =>
        moment(a.last_modified_at).isSameOrAfter(b.last_modified_at) ? -1 : 1
      )[0]?.last_modified_at;
      const { site_id, usages, language_group_id } = languages[0];
      let languagesLabel = '';
      let bodies = '';
      languages.forEach(item => {
        item.languageLabel = this.basicService.translate(item.language);
        languagesLabel += `#${item.languageLabel}`;
        bodies += `#${item.body}`;
      });
      const formatLabel = (value, key) => (value ? this.basicService.translate(key, value) : 'N/C');
      const categoryLabel = formatLabel(category, `parameters/kakaotalk-templates/category/${category}/label`);
      const lastModificationDateLabel = lastModificationDate?.length
        ? this.dateTimezonePipe.transform(lastModificationDate, 'shortDate')
        : '';

      const accountName = accounts.find(a => a.id === site_id)?.name || 'N/C';
      const templates = (languages || []).map(l => {
        const { id, body, language, displayed_placeholders, buttons, header } = l;
        const buttonsFormatted = (buttons || []).map((button: KakaotalkTemplateButton) => {
          this.formatButtonUrl(button);
          return button;
        });
        Object.keys(displayed_placeholders || {}).forEach(key => {
          if (!displayed_placeholders[key]) {
            displayed_placeholders[key] = '';
          }
        });

        return {
          code: language,
          active: true,
          template: {
            id,
            header,
            body,
            buttons: buttonsFormatted,
            placeholders: displayed_placeholders,
          },
        };
      });

      const formatDate = (date: string): string => this.dateTimezonePipe.transform(date, 'short');
      const metadata = {
        ...languages[0],
        languages,
        detailsUrl: this.getTemplateDetailsPath(`${name}`, site_id),
        category,
        categoryLabel,
        lastModificationDate,
        lastModificationDateLabel,
        accountName,
        summary: `${name}#${accountName}#${categoryLabel}#${lastModificationDateLabel}${languagesLabel}${bodies}`,
        created_by: languages[0].created_by,
        last_modified_by: languages[0].last_modified_by,
        created_at: formatDate(languages[0].created_at),
        last_modified_at: formatDate(languages[0].last_modified_at),
        usages,
        language_group_id,
      };
      templatesAggregatted.push({
        metadata,
        // Same model than the form.
        main: {
          name,
          languages: (languages || []).map(({ language }) => language),
          siteId: site_id,
          usages,
        },
        category,
        templates,
      });
    });
    return templatesAggregatted;
  };

  findOne(siteId: number, uuid: string) {
    return this.findAccounts().pipe(
      switchMap(accounts => {
        return this.httpClient
          .get<KakaotalkTemplateItem[]>(
            `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/kakaotalk/templates/${uuid}`
          )
          .pipe(
            map(templates => {
              if (templates?.length) {
                return this.formatTemplates(templates, accounts)[0];
              }
              this.topToolbarService.updateButtonDisable(true);
              this.navigateToList();
            }),
            this.errorHandlingService.catchAlertAndThrowHttpError()
          );
      })
    );
  }

  save(data: KakaotalkTemplateForm, languageGroupId?: string): Observable<any> {
    const { main, templates, category } = data;
    const { siteId, usages, name } = main;
    const model: any[] = (templates || [])
      .filter(({ active }) => active)
      .map(({ template, code }) => {
        const {
          id,
          body,
          buttons = undefined,
          placeholders: displayed_placeholders = undefined,
          header = {} as any,
        } = template;

        if (header) {
          delete header.media_url;
          delete header.filename;
        }
        const item: KakaotalkTemplate = {
          id: languageGroupId ? id : undefined,
          name,
          language: code,
          siteId,
          usages,
          body,
          category,
          header,
        };
        if (Object.keys(displayed_placeholders || {})?.length) {
          item.displayed_placeholders = displayed_placeholders;
        }
        if (buttons?.length) {
          item.buttons = buttons;
        }
        return item;
      });

    if (!languageGroupId) {
      return this.httpClient
        .post(
          `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/kakaotalk/templates/create`,
          model
        )
        .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
    }
    return this.httpClient
      .put(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/kakaotalk/templates/${languageGroupId}`,
        model
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getSupportedLanguages() {
    return [
      { label: 'Chinese (CHN)', id: 'zh_CN' },
      { label: 'Chinese (HKG)', id: 'zh_HK' },
      { label: 'Chinese (TAI)', id: 'zh_TW' },
      { label: 'Dutch', id: 'nl' },
      { label: 'English', id: 'en' },
      { label: 'French', id: 'fr' },
      { label: 'German', id: 'de' },
      { label: 'Italian', id: 'it' },
      { label: 'Japanese', id: 'ja' },
      { label: 'Korean', id: 'ko' },
      { label: 'Spanish', id: 'es' },
    ]
      .map(l => {
        const result = this.basicService.translate(l.id);
        const label = result === l.id ? l.label : result;
        return { id: l.id, label };
      })
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  remove(siteId: number, id: number) {
    return this.httpClient
      .delete(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/sites/${siteId}/kakaotalk/templates/${id}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getAnswerTemplatesForEngagement(siteId: number): Observable<KakaotalkTemplate[]> {
    return this.httpClient
      .post<KakaotalkTemplate[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/kakaotalk/templates`, {
        sort: { active: 'name', direction: 'asc' },
        range: 99999,
        accountIds: [siteId],
      })
      .pipe(
        map(response => {
          (response || []).forEach(kakaotalkTemplate => {
            kakaotalkTemplate.textWithReplacedPlaceholders = kakaotalkTemplate.body;
            kakaotalkTemplate.parameters = [];
            kakaotalkTemplate.placeholders = kakaotalkTemplate.displayed_placeholders;
            for (const key of Object.keys(kakaotalkTemplate.placeholders || {})) {
              const index = Number(key.match(/{{([0-9]+)}}/)[1]) - 1; // placeholders start at {{1}}
              kakaotalkTemplate.parameters[index] = {
                placeholder: kakaotalkTemplate.placeholders[key]?.label || kakaotalkTemplate.placeholders[key],
                value: '',
              };
            }
            if (kakaotalkTemplate.header?.media_id) {
              kakaotalkTemplate.header.type = 'media';
              const { media_id } = kakaotalkTemplate.header;
              if (media_id) {
                kakaotalkTemplate.header.media_url = this.uploadService.pictureUrl(+media_id);
              }
            }
          });
          return response || [];
        })
      );
  }
}
